import { createSelector } from '@ngrx/store';
import { PlatformEnum } from '@panamax/app-state';
import { Product } from '@shared/models/product.model';
import { productSelector } from '@shared/selectors/product.selectors';
import {
  pickupRequestLookup,
  recoveryOrderLookup,
  requestCreditLookup,
  selectSupportCenterFilter,
  selectSupportCenterItemsAndCountsByFilter,
  selectSupportCenterSort,
  selectSupportCenterState,
  supportRequestLookup,
} from '@usf/ngrx-list';
import { SupportCenterPageViewModel } from '../model/support-center-model';
import {
  getSupportCenterItemSize,
  sortSupportCenterItems,
  transformSupportCenterItemToViewModel,
  transformSupportCenterSort,
} from './helpers/support-center-selector-helpers';

export const selectSupportCenterViewModel = (platform: PlatformEnum) => {
  return createSelector(
    productSelector,
    selectSupportCenterItemsAndCountsByFilter,
    selectSupportCenterFilter,
    selectSupportCenterSort,
    selectSupportCenterState,
    requestCreditLookup(),
    pickupRequestLookup(),
    recoveryOrderLookup(),
    supportRequestLookup(),
    (
      products,
      rawItemsAndCounts,
      lastSelectedFilter,
      rawSupportCenterSort,
      supportCenterState,
      creditLookup,
      pickupLookup,
      recoveryLookup,
      supportLookup,
    ) => {
      const selectedHeader = rawSupportCenterSort.selectedHeader;
      const itemsToSort = rawItemsAndCounts.supportItems.map(item => {
        let product = products.get(Number(item.productNumber).valueOf());
        if (!!product?.summary) {
          return transformSupportCenterItemToViewModel(
            item,
            product,
            creditLookup,
            pickupLookup,
            recoveryLookup,
            supportLookup,
          );
        } else {
          return transformSupportCenterItemToViewModel(
            item,
            {
              productNumber: item.productNumber,
              imageThumbnail: undefined,
            } as unknown as Product,
            creditLookup,
            pickupLookup,
            recoveryLookup,
            supportLookup,
          );
        }
      });
      const supportItems = sortSupportCenterItems(
        selectedHeader,
        rawSupportCenterSort.headers[selectedHeader].sortDirection,
        itemsToSort,
      );
      const supportCenterSort = {
        selectedHeader: rawSupportCenterSort.selectedHeader,
        headers: transformSupportCenterSort(
          rawSupportCenterSort.headers,
          platform,
        ),
      };
      let tabsIndex = 0;
      let tabsMap = {};
      Object.keys(rawItemsAndCounts.counts).forEach((tab: string) => {
        if (rawItemsAndCounts.counts[tab] > 0) {
          tabsMap[tab] = tabsIndex++;
        }
      });
      return {
        supportItems,
        lastSelectedFilter,
        itemSize: getSupportCenterItemSize(platform),
        filterCounts: rawItemsAndCounts.counts,
        supportCenterSort,
        isLoaded: supportCenterState.loaded,
        tabsMap,
      } as SupportCenterPageViewModel;
    },
  );
};

export const selectSupportCenterProductsToLoad = () =>
  createSelector(selectSupportCenterState, supportCenterState => {
    const ids = supportCenterState.ids;
    const entities = supportCenterState.entities;
    const productsToLoad: number[] = [];
    ids.forEach(id => {
      const entity = entities[id];
      if (!!entity && !!entity.productNumber) {
        productsToLoad.push(Number(entity.productNumber).valueOf());
      }
    });
    return { products: productsToLoad, isLoaded: supportCenterState.loaded };
  });
